import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ApiErrorCodes } from '../../../../core/enums/ErrorCodes';
import { LocalStorageService } from '../../../../core/services/LocalStorageService';
import { NotificationService } from '../../../../core/services/NotificationService';
import { CartItemTypes } from '../enums/CartItemTypes';
import { DiscountType } from '../enums/DiscountTypes';
import { CartStore } from '../stores/CartStore';
import { PaySeparatelyStore } from '../stores/PaySeparatelyStore';

@Injectable({
  providedIn: 'root'
})
export class PaySeparatelyService {

  public readonly paySeparatelyModeChanged$ = new Subject<boolean>();

  public readonly paySeparatelyStore = inject(PaySeparatelyStore);
  public readonly cartStore = inject(CartStore);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _notificationService = inject(NotificationService);

  public initFromStorage(): void {
    const stored = this._localStorageService.getItem(PaySeparatelyStore.MODE_STORAGE_KEY);
    const enabled = stored === true || stored === 'true';

    if (enabled && this.hasDiscountCode()) {
      this.disablePaySeparatelyMode();
      return;
    }

    this.setPaySeparatelyMode(enabled);
  }

  public get isPartialPaymentPossible(): boolean {
    if (this.hasDiscountCode()) {
      return false;
    }

    const items = this.cartStore.cart().items.filter(item =>
      (item.itemType !== CartItemTypes.CashCineCard && item.itemType !== CartItemTypes.CashVoucher)
    );

    if (items.length > 1) {
      return true;
    }

    if (items.length === 1) {
      return items[0].quantity > 1;
    }

    return false;
  }

  public enablePaySeparatelyMode(): void {
    const cart = this.cartStore.cart();

    if (!cart) return;

    const hasCancelItems = cart.items.some(item =>
      item.itemType === CartItemTypes.CancelTicket ||
      item.itemType === CartItemTypes.CancelVoucher ||
      item.itemType === CartItemTypes.CancelProduct ||
      item.itemType === CartItemTypes.CancelPackage ||
      item.itemType === CartItemTypes.CancelCard
    );

    if (hasCancelItems) {
      this._notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Partial checkout is not allowed when basket contains cancel items.'.trans('cart/summary-modal/notifications/partial-checkout-not-allowed-with-cancel-items.Message')
      );
      return;
    }

    if (this.hasDiscountCode()) {
      this._notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        ApiErrorCodes.PartialCheckoutNotAllowedWithDiscountCode.trans(
          `errors/notifications/${ApiErrorCodes.PartialCheckoutNotAllowedWithDiscountCode}.Message`
        )
      );
      return;
    }

    this.setPaySeparatelyMode(true);
    this._localStorageService.setItem(PaySeparatelyStore.MODE_STORAGE_KEY, true);
    this.paySeparatelyStore.setNone();
  }

  public disablePaySeparatelyMode(): void {
    this.paySeparatelyStore.reset();
    this.paySeparatelyModeChanged$.next(false);
  }

  public setPaySeparatelyMode(isPaySeparatelyModeEnabled: boolean): void {
    this.paySeparatelyStore.isPaySeparatelyModeEnabled.set(isPaySeparatelyModeEnabled);
    this.paySeparatelyModeChanged$.next(isPaySeparatelyModeEnabled);
  }

  private hasDiscountCode(): boolean {
    return this.cartStore.cart()?.discount?.type === DiscountType.DiscountCode;
  }
}
